import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ExportPreflightIssue,
  ExportPreflightReport,
  ExportProofArtifact,
  ExportProofModalPhase
} from '../types/exportPreflight';
import './ExportProofModal.css';

export interface ExportProofModalProps {
  phase: ExportProofModalPhase;
  report?: ExportPreflightReport | null;
  artifact?: ExportProofArtifact | null;
  error?: string | null;
  onClose: () => void;
  onRetryPreflight?: () => void | Promise<void>;
  onGenerateProof?: (report: ExportPreflightReport) => void | Promise<void>;
  onGenerateAnyway?: (report: ExportPreflightReport) => void | Promise<void>;
  layoutOverrideApplied?: boolean;
  onGoToIssue?: (issue: ExportPreflightIssue) => void;
  onDownloadPdf?: (artifact: ExportProofArtifact, resolvedSource: string) => void;
  onDownloadDiagnostics?: (report: ExportPreflightReport) => void;
}

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  '[href]',
  'iframe',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const EXPORT_LABELS = {
  full: 'Complete book',
  manuscript: 'Manuscript',
  'front-cover': 'Front cover',
  'back-cover': 'Back cover'
} as const;

const formatInches = (value: number) => `${Number(value.toFixed(3))}\u2033`;

const formatBytes = (value?: number) => {
  if (!value || value < 1) return '';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const targetLabel = (issue: ExportPreflightIssue) => {
  if (issue.pageIndex !== undefined) return `Page ${issue.pageIndex + 1}`;
  if (issue.scope === 'element') return 'Page element';
  if (issue.scope === 'page') return 'Page';
  return 'Whole book';
};

const metricsLabel = (issue: ExportPreflightIssue) => {
  if (!issue.metrics) return '';
  return Object.entries(issue.metrics)
    .map(([name, value]) => `${name.replace(/([A-Z])/g, ' $1')}: ${String(value)}`)
    .join(' · ');
};

const createDownload = (source: string, filename: string) => {
  const anchor = document.createElement('a');
  anchor.href = source;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const ExportProofModal: React.FC<ExportProofModalProps> = ({
  phase,
  report,
  artifact,
  error,
  onClose,
  onRetryPreflight,
  onGenerateProof,
  onGenerateAnyway,
  layoutOverrideApplied = false,
  onGoToIssue,
  onDownloadPdf,
  onDownloadDiagnostics
}) => {
  const dialogRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  const [proofLoaded, setProofLoaded] = useState(false);
  const [acknowledgedLayoutReportId, setAcknowledgedLayoutReportId] = useState<string | null>(null);
  const [submittingLayoutOverride, setSubmittingLayoutOverride] = useState(false);
  const [objectUrlState, setObjectUrlState] = useState<{
    blob: Blob;
    url: string;
  } | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!artifact || artifact.kind !== 'blob') {
      setObjectUrlState(null);
      return undefined;
    }

    const nextUrl = URL.createObjectURL(artifact.blob);
    setObjectUrlState({ blob: artifact.blob, url: nextUrl });
    return () => URL.revokeObjectURL(nextUrl);
  }, [artifact]);

  // Never pair a newly rendered artifact identity with the previous Blob URL
  // during the one render before its effect creates a replacement URL.
  const proofSource = artifact?.kind === 'url'
    ? artifact.url
    : artifact?.kind === 'blob' && objectUrlState?.blob === artifact.blob
      ? objectUrlState.url
      : null;
  const artifactMatchesReport = Boolean(
    artifact
      && report
      && artifact.reportId === report.reportId
      && artifact.inputHash === report.inputHash
      && artifact.bookRevision === report.bookRevision
  );
  const proofReady = phase === 'ready'
    && Boolean(proofSource)
    && artifactMatchesReport;
  const reportIdentity = report
    ? `${report.reportId}:${report.inputHash}:${report.bookRevision}`
    : '';

  useEffect(() => {
    setProofLoaded(false);
  }, [proofSource]);

  useEffect(() => {
    setAcknowledgedLayoutReportId(null);
    setSubmittingLayoutOverride(false);
  }, [reportIdentity]);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(element => !element.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;
      if (!dialogRef.current.contains(activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.contains(document.activeElement)) {
      closeButtonRef.current?.focus();
    }
  }, [phase]);

  const sortedIssues = useMemo(() => [...(report?.issues || [])].sort((first, second) => {
    if (first.severity !== second.severity) return first.severity === 'error' ? -1 : 1;
    return (first.pageIndex ?? -1) - (second.pageIndex ?? -1);
  }), [report]);
  const blockingIssues = useMemo(
    () => sortedIssues.filter(issue => issue.severity === 'error'),
    [sortedIssues]
  );
  const canOverrideLayout = Boolean(
    report
      && phase === 'blocked'
      && onGenerateAnyway
      && blockingIssues.length > 0
      && blockingIssues.length === report.summary.errorCount
      && blockingIssues.every(issue => issue.bypassable === true)
  );
  const layoutOverrideAcknowledged = Boolean(
    report && acknowledgedLayoutReportId === report.reportId
  );
  const generatedWithAcknowledgedLayoutIssues = Boolean(
    report
      && phase === 'ready'
      && report.summary.status === 'blocked'
      && layoutOverrideApplied
  );

  const downloadDiagnostics = () => {
    if (!report) return;
    if (onDownloadDiagnostics) {
      onDownloadDiagnostics(report);
      return;
    }
    const diagnostics = new Blob([JSON.stringify(report, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(diagnostics);
    createDownload(url, `${report.title || 'storybook'}-export-diagnostics.json`);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const downloadPdf = () => {
    if (!artifact || !proofSource || !proofReady || !proofLoaded) return;
    if (onDownloadPdf) {
      onDownloadPdf(artifact, proofSource);
      return;
    }
    createDownload(proofSource, artifact.filename);
  };

  const generateAnyway = async () => {
    if (!report
      || !canOverrideLayout
      || !layoutOverrideAcknowledged
      || submittingLayoutOverride
      || !onGenerateAnyway) return;
    setSubmittingLayoutOverride(true);
    try {
      await onGenerateAnyway(report);
    } finally {
      setSubmittingLayoutOverride(false);
    }
  };

  const heading = phase === 'blocked'
    ? 'Export needs attention'
    : phase === 'ready'
      ? 'Review your PDF proof'
      : phase === 'error'
        ? 'Export proof unavailable'
        : phase === 'generating'
          ? 'Building your PDF proof'
          : 'Checking every page';

  const statusLabel = phase === 'blocked'
    ? canOverrideLayout ? 'Needs acknowledgment' : 'Blocked'
    : phase === 'ready'
      ? generatedWithAcknowledgedLayoutIssues ? 'Proof ready · issues acknowledged' : 'Proof ready'
      : phase === 'error'
        ? 'Could not finish'
        : phase === 'generating'
          ? 'Rendering proof'
          : 'Running preflight';

  const canGenerateProof = Boolean(
    report
      && report.summary.status === 'ready'
      && phase !== 'generating'
      && phase !== 'ready'
      && onGenerateProof
  );

  return (
    <div
      className="export-proof-overlay"
      role="presentation"
      data-testid="export-proof-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="export-proof-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-proof-title"
        aria-describedby="export-proof-description"
        data-testid="export-proof-modal"
        data-phase={phase}
      >
        <header className="export-proof-header">
          <div>
            <span className={`export-proof-status ${phase}`} data-testid="export-proof-status">
              {statusLabel}
            </span>
            <h2 id="export-proof-title">{heading}</h2>
            <p id="export-proof-description">
              The preview and download use the exact same PDF artifact. Check the proof before saving it.
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="export-proof-close"
            aria-label="Close export proof"
            data-testid="export-proof-close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="export-proof-body">
          <section className="export-proof-viewer" aria-labelledby="export-proof-viewer-title">
            <div className="export-proof-viewer-heading">
              <div>
                <span className="export-proof-step">Final check</span>
                <h3 id="export-proof-viewer-title">PDF proof</h3>
              </div>
              {artifact && artifactMatchesReport && (
                <span className="export-proof-artifact-meta">
                  {artifact.filename}{formatBytes(artifact.byteLength) ? ` · ${formatBytes(artifact.byteLength)}` : ''}
                </span>
              )}
            </div>

            {proofReady && proofSource ? (
              <div className={`export-proof-frame-wrap ${proofLoaded ? 'is-loaded' : 'is-loading'}`}>
                {!proofLoaded && (
                  <div className="export-proof-frame-loading" role="status">
                    <span className="export-proof-spinner" aria-hidden="true" />
                    Loading the exact PDF proof…
                  </div>
                )}
                <iframe
                  className="export-proof-frame"
                  src={proofSource}
                  title={`PDF proof for ${report?.title || 'storybook'}`}
                  referrerPolicy="no-referrer"
                  data-testid="export-proof-frame"
                  onLoad={() => setProofLoaded(true)}
                />
              </div>
            ) : (
              <div className={`export-proof-placeholder ${phase}`} data-testid="export-proof-placeholder">
                {phase === 'checking' && <span className="export-proof-spinner" aria-hidden="true" />}
                {phase === 'generating' && <span className="export-proof-spinner" aria-hidden="true" />}
                <strong>
                  {phase === 'blocked'
                    ? canOverrideLayout
                      ? 'Layout issues need your decision'
                      : 'Proof generation is paused'
                    : phase === 'error'
                      ? 'No PDF was created'
                      : phase === 'generating'
                        ? 'Rendering with the final PDF engine'
                        : 'Running export preflight'}
                </strong>
                <p>
                  {phase === 'blocked'
                    ? canOverrideLayout
                      ? 'These visual layout and readability issues can appear in the PDF. Fix them, or explicitly acknowledge them below to create a proof anyway.'
                      : 'Resolve every blocking issue, then run preflight again. No outdated PDF will be offered.'
                    : phase === 'error'
                      ? (error || 'The export could not be completed. Your book is still saved.')
                      : phase === 'generating'
                        ? 'The first page will appear here when the exact downloadable artifact is ready.'
                        : 'Checking page order, trim, bleed, fonts, image resolution, and snapshot identity.'}
                </p>
                {canGenerateProof && report && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid="export-generate-proof"
                    onClick={() => void onGenerateProof?.(report)}
                  >
                    Generate PDF proof
                  </button>
                )}
              </div>
            )}

            {report && (
              <div className="export-proof-trace" aria-label="Proof identity">
                <span><strong>Book revision</strong> {report.bookRevision}</span>
                <span><strong>Report</strong> {report.reportId}</span>
                <span title={report.inputHash}><strong>Snapshot</strong> {report.inputHash.slice(0, 12)}</span>
                <span><strong>Renderer</strong> {report.renderer.version}</span>
              </div>
            )}
          </section>

          <aside className="export-proof-checks" aria-label="Export checks">
            <div className="export-proof-checks-heading">
              <div>
                <span className="export-proof-step">Preflight</span>
                <h3>Print readiness</h3>
              </div>
              {report && (
                <span className={`export-proof-result ${generatedWithAcknowledgedLayoutIssues ? 'acknowledged' : report.summary.status}`}>
                  {report.summary.status === 'ready'
                    ? 'Ready'
                    : generatedWithAcknowledgedLayoutIssues
                      ? 'Generated anyway'
                      : 'Blocked'}
                </span>
              )}
            </div>

            {!report ? (
              <div className="export-proof-checking" role="status" aria-live="polite">
                <span className="export-proof-spinner" aria-hidden="true" />
                <span>{phase === 'error' ? (error || 'Preflight unavailable.') : 'Inspecting your saved book…'}</span>
              </div>
            ) : (
              <>
                <div className="export-proof-summary-grid" data-testid="export-proof-summary">
                  <article className="export-proof-summary-card">
                    <span>Page order</span>
                    <strong>{report.selection.pageCount} pages</strong>
                    <small>{EXPORT_LABELS[report.selection.exportType]}</small>
                  </article>
                  <article className={`export-proof-summary-card ${report.geometry.supported ? 'passed' : 'failed'}`}>
                    <span>Trim</span>
                    <strong>{formatInches(report.geometry.trimBox.width)} × {formatInches(report.geometry.trimBox.height)}</strong>
                    <small>{report.geometry.canvasSize}</small>
                  </article>
                  <article className="export-proof-summary-card">
                    <span>Bleed & safe area</span>
                    <strong>{formatInches(report.geometry.bleedInches)} bleed</strong>
                    <small>{formatInches(report.geometry.safeMarginInches)} safe margin</small>
                  </article>
                  <article className={`export-proof-summary-card ${report.fonts.status === 'ready' ? 'passed' : 'failed'}`}>
                    <span>Fonts</span>
                    <strong>{report.fonts.requested.length} faces ready</strong>
                    <small>Assets {report.fonts.assetVersion}</small>
                  </article>
                  <article className={`export-proof-summary-card ${report.images.blocking ? 'failed' : report.images.unknown || report.images.belowTarget ? 'warning' : 'passed'}`}>
                    <span>Image DPI</span>
                    <strong>{report.images.verified}/{report.images.total} verified</strong>
                    <small>{report.images.belowTarget} low · {report.images.unknown} unknown</small>
                  </article>
                </div>

                <div className="export-proof-issue-summary" aria-live="polite">
                  <span className="errors" data-testid="export-proof-error-count">
                    {report.summary.errorCount} {generatedWithAcknowledgedLayoutIssues ? 'acknowledged' : 'blocking'}
                  </span>
                  <span className="warnings" data-testid="export-proof-warning-count">
                    {report.summary.warningCount} warnings
                  </span>
                </div>

                {sortedIssues.length > 0 ? (
                  <div className="export-proof-issues" data-testid="export-proof-issues">
                    {sortedIssues.map((issue, index) => {
                      const metricText = metricsLabel(issue);
                      return (
                        <article
                          key={`${issue.code}:${issue.pageId || 'book'}:${issue.elementId || index}`}
                          className={`export-proof-issue ${issue.severity}`}
                          data-testid="export-proof-issue"
                          data-severity={issue.severity}
                        >
                          <div className="export-proof-issue-title">
                            <span>
                              {issue.severity === 'error'
                                ? generatedWithAcknowledgedLayoutIssues ? 'Acknowledged' : 'Blocking'
                                : 'Warning'}
                            </span>
                            <code>{issue.code}</code>
                            <small>{targetLabel(issue)}</small>
                          </div>
                          <p>{issue.message}</p>
                          <p className="export-proof-suggestion"><strong>How to fix:</strong> {issue.suggestion}</p>
                          {metricText && <p className="export-proof-metrics">{metricText}</p>}
                          {onGoToIssue && (issue.pageId || issue.scope === 'book') && (
                            <button
                              type="button"
                              className="export-proof-issue-action"
                              data-testid="export-proof-go-to-issue"
                              onClick={() => onGoToIssue(issue)}
                            >
                              {issue.pageId ? 'Open affected page' : 'Review book settings'}
                            </button>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="export-proof-clean" data-testid="export-proof-clean">
                    <span aria-hidden="true">✓</span>
                    <div>
                      <strong>All export checks passed</strong>
                      <p>The saved snapshot is ready for a reproducible PDF proof.</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </aside>
        </div>

        <footer className="export-proof-footer">
          <div
            className={`export-proof-footer-message${canOverrideLayout ? ' has-override' : ''}`}
          >
            {phase === 'ready'
              ? (
                <span role="status" aria-live="polite">
                  {proofLoaded
                    ? generatedWithAcknowledgedLayoutIssues
                      ? 'Proof loaded with the layout issues you acknowledged. Review it carefully before downloading.'
                      : 'Proof loaded. The exact PDF is ready to download.'
                    : generatedWithAcknowledgedLayoutIssues
                      ? 'Loading the exact PDF proof with your acknowledged layout issues…'
                      : 'Loading the exact PDF proof…'}
                </span>
              )
              : phase === 'blocked'
                ? canOverrideLayout
                  ? (
                    <label className="export-proof-override-acknowledgement">
                      <input
                        type="checkbox"
                        data-testid="export-acknowledge-layout-issues"
                        checked={layoutOverrideAcknowledged}
                        onChange={(event) => setAcknowledgedLayoutReportId(
                          event.target.checked && report ? report.reportId : null
                        )}
                      />
                      <span>I understand these layout and readability issues will appear in the PDF.</span>
                    </label>
                  )
                  : <span role="status" aria-live="polite">PDF download stays unavailable while blocking issues remain.</span>
                : phase === 'error'
                  ? <span role="status" aria-live="polite">{error || 'Export could not be completed.'}</span>
                  : phase === 'generating'
                    ? <span role="status" aria-live="polite">Creating the final PDF artifact…</span>
                    : <span role="status" aria-live="polite">Waiting for a complete preflight…</span>}
          </div>
          <div className="export-proof-footer-actions">
            {report && (
              <button
                type="button"
                className="btn btn-secondary"
                data-testid="export-download-diagnostics"
                onClick={downloadDiagnostics}
              >
                Download diagnostics
              </button>
            )}
            {(phase === 'blocked' || phase === 'error') && onRetryPreflight && (
              <button
                type="button"
                className="btn btn-secondary"
                data-testid="export-retry-preflight"
                onClick={() => void onRetryPreflight()}
              >
                Run preflight again
              </button>
            )}
            {canOverrideLayout && (
              <button
                type="button"
                className="btn btn-primary export-generate-anyway"
                data-testid="export-generate-anyway"
                disabled={!layoutOverrideAcknowledged || submittingLayoutOverride}
                onClick={() => void generateAnyway()}
                title={!layoutOverrideAcknowledged
                  ? 'Acknowledge the reported layout and readability issues first.'
                  : undefined}
              >
                {submittingLayoutOverride ? 'Starting PDF…' : 'Generate PDF anyway'}
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary export-proof-download"
              data-testid="export-download-pdf"
              disabled={!proofReady || !proofLoaded}
              onClick={downloadPdf}
              title={!proofReady || !proofLoaded ? 'Review the finished PDF proof before downloading.' : undefined}
            >
              {proofReady && !proofLoaded ? 'Loading proof…' : 'Download PDF'}
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default ExportProofModal;
